import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Loader2,
  Briefcase,
  TrendingUp,
  FileSignature,
  ClipboardCheck,
  ShieldCheck,
  CircleDashed,
  CalendarClock,
} from "lucide-react";
import { toast } from "sonner";
import { FIRM } from "../components/site/_shared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CALENDLY_URL = "https://calendly.com/oejibe-charis-mettle";

const DEAL_TYPES = [
  { value: "M&A", label: "Mergers & Acquisitions", icon: Briefcase },
  { value: "Private Equity", label: "Private Equity", icon: TrendingUp },
  { value: "Commercial Transaction", label: "Commercial Transaction", icon: FileSignature },
  { value: "Regulatory", label: "Regulatory Compliance", icon: ShieldCheck },
  { value: "Corporate Governance", label: "Corporate Governance", icon: ClipboardCheck },
  { value: "Other", label: "Something else", icon: CircleDashed },
];

const TIMELINES = [
  { value: "Within 30 days", label: "Within 30 days", note: "Active matter, urgent." },
  { value: "1-3 months", label: "1–3 months", note: "Negotiations underway." },
  { value: "3-6 months", label: "3–6 months", note: "Planning the transaction." },
  { value: "Exploratory", label: "Exploratory", note: "Gathering options & advice." },
];

const STEP_TITLES = ["Deal type", "Timeline", "Counterparty", "About you"];

const initialForm = {
  deal_type: "",
  timeline: "",
  counterparty_name: "",
  jurisdiction: "",
  context: "",
  name: "",
  email: "",
  company: "",
  phone: "",
};

export default function IntakePage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0); // 0..3 form steps, 4 = success/calendar
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // { id, lead_score, calendly_url }

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [step]);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const setField = (name, value) => setForm((f) => ({ ...f, [name]: value }));

  const canAdvance = useMemo(() => {
    if (step === 0) return !!form.deal_type;
    if (step === 1) return !!form.timeline;
    if (step === 2) return true; // optional
    if (step === 3) return !!form.name.trim() && !!form.email.trim();
    return true;
  }, [step, form]);

  const next = () => {
    if (!canAdvance) {
      toast.error("Please make a selection to continue.");
      return;
    }
    setStep((s) => Math.min(s + 1, 3));
  };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Name and email are required.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/intake`, form);
      if (res.data?.success) {
        setResult(res.data);
        setStep(4);
        toast.success("Matter intake received.");
      } else {
        toast.error("Submission failed. Please try again.");
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Submission failed. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  const progress = step >= 4 ? 100 : ((step + 1) / 4) * 100;

  return (
    <main data-testid="intake-page" className="min-h-screen bg-cream-100 text-charcoal">
      {/* Top bar */}
      <header className="border-b border-cream-300 bg-cream-100/85 backdrop-blur-xl sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <Link
            to="/"
            data-testid="intake-home-link"
            className="font-serif text-xl lg:text-2xl tracking-tight text-sage-600"
          >
            Charis <span className="italic">&amp;</span> Mettle
          </Link>
          <Link
            to="/"
            data-testid="intake-close-link"
            className="font-sans text-[12px] uppercase tracking-[0.22em] text-charcoal-muted hover:text-sage-700 transition-colors"
          >
            Close
          </Link>
        </div>
      </header>

      <section className="max-w-6xl mx-auto px-6 lg:px-10 pt-12 lg:pt-20 pb-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Left rail */}
          <aside className="lg:col-span-4 lg:sticky lg:top-28 lg:self-start">
            <span className="text-[11px] uppercase tracking-[0.28em] text-sage-600 font-sans">
              Matter Intake
            </span>
            <h1 className="mt-4 font-serif text-4xl lg:text-5xl leading-[1.05] tracking-tight">
              Tell us about
              <span className="block italic text-sage-600">your matter.</span>
            </h1>
            <p className="mt-6 text-charcoal-muted font-sans leading-relaxed">
              Four short steps; then choose a time on the calendar. Everything
              you share is confidential, even if no formal engagement follows.
            </p>

            <ol className="mt-12 space-y-4">
              {STEP_TITLES.map((t, i) => {
                const status = step > i || step === 4 ? "done" : step === i ? "active" : "pending";
                return (
                  <li
                    key={t}
                    data-testid={`step-indicator-${i}`}
                    data-status={status}
                    className={`flex items-center gap-4 font-sans text-[14px] transition-colors ${
                      status === "active"
                        ? "text-charcoal"
                        : status === "done"
                        ? "text-sage-700"
                        : "text-charcoal-muted/70"
                    }`}
                  >
                    <span
                      className={`w-8 h-8 inline-flex items-center justify-center border text-[12px] ${
                        status === "done"
                          ? "bg-sage-600 border-sage-600 text-cream-50"
                          : status === "active"
                          ? "border-sage-600 text-sage-600"
                          : "border-cream-300 text-charcoal-muted"
                      }`}
                    >
                      {status === "done" ? <Check size={14} /> : `0${i + 1}`}
                    </span>
                    {t}
                  </li>
                );
              })}
            </ol>
          </aside>

          {/* Right panel */}
          <div className="lg:col-span-8">
            {/* Progress bar */}
            <div className="h-px bg-cream-300 w-full overflow-hidden">
              <div
                data-testid="intake-progress"
                className="h-full bg-sage-600 transition-all duration-500"
                style={{ width: `${progress}%` }}
              />
            </div>
            <p className="mt-3 text-[11px] uppercase tracking-[0.22em] text-charcoal-muted font-sans">
              {step >= 4 ? "Complete" : `Step ${step + 1} of 4`}
            </p>

            <div className="mt-10 min-h-[420px]">
              <AnimatePresence mode="wait">
                <motion.div
                  key={step}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                >
                  {step === 0 && (
                    <Step
                      eyebrow="Step 01"
                      title="What kind of matter brings you here?"
                      hint="Choose the closest fit. We can refine on the call."
                    >
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {DEAL_TYPES.map(({ value, label, icon: Icon }) => {
                          const active = form.deal_type === value;
                          return (
                            <button
                              type="button"
                              key={value}
                              data-testid={`option-deal-${value.replace(/[^a-z0-9]/gi, "-")}`}
                              onClick={() => setField("deal_type", value)}
                              className={`group flex items-center gap-4 p-5 border text-left transition-all duration-300 ${
                                active
                                  ? "border-sage-600 bg-sage-600/[0.06]"
                                  : "border-cream-300 hover:border-sage-600/60 bg-cream-50"
                              }`}
                            >
                              <Icon
                                size={22}
                                strokeWidth={1.5}
                                className={active ? "text-sage-600" : "text-charcoal-muted"}
                              />
                              <span className="font-serif text-lg leading-tight">{label}</span>
                            </button>
                          );
                        })}
                      </div>
                    </Step>
                  )}

                  {step === 1 && (
                    <Step
                      eyebrow="Step 02"
                      title="What's the timeline?"
                      hint="Honest is better than aspirational."
                    >
                      <div className="space-y-3">
                        {TIMELINES.map(({ value, label, note }) => {
                          const active = form.timeline === value;
                          return (
                            <button
                              type="button"
                              key={value}
                              data-testid={`option-timeline-${value.replace(/[^a-z0-9]/gi, "-")}`}
                              onClick={() => setField("timeline", value)}
                              className={`w-full flex items-center justify-between p-5 border text-left transition-all duration-300 ${
                                active
                                  ? "border-sage-600 bg-sage-600/[0.06]"
                                  : "border-cream-300 hover:border-sage-600/60 bg-cream-50"
                              }`}
                            >
                              <div>
                                <p className="font-serif text-xl leading-tight">{label}</p>
                                <p className="mt-1 text-charcoal-muted text-sm font-sans">{note}</p>
                              </div>
                              <span
                                className={`w-5 h-5 border ${
                                  active ? "border-sage-600 bg-sage-600" : "border-charcoal-muted/40"
                                }`}
                              />
                            </button>
                          );
                        })}
                      </div>
                    </Step>
                  )}

                  {step === 2 && (
                    <Step
                      eyebrow="Step 03"
                      title="Tell us about the counterparty."
                      hint="Optional. Helps us check for conflicts before we meet."
                    >
                      <div className="space-y-8">
                        <Field
                          label="Counterparty name"
                          name="counterparty_name"
                          value={form.counterparty_name}
                          onChange={onChange}
                          placeholder="e.g. Northbridge Capital"
                          testid="intake-input-counterparty"
                        />
                        <div>
                          <label className="text-[11px] uppercase tracking-[0.22em] text-charcoal-muted font-sans block">
                            Jurisdiction
                          </label>
                          <div className="mt-3 flex flex-wrap gap-2">
                            {["Lagos", "Abuja", "Port Harcourt", "Nigeria - Other", "Other"].map((j) => {
                              const active = form.jurisdiction === j;
                              return (
                                <button
                                  type="button"
                                  key={j}
                                  data-testid={`option-jurisdiction-${j.replace(/\s+/g, "-")}`}
                                  onClick={() => setField("jurisdiction", j)}
                                  className={`px-4 py-2 border font-sans text-[13px] transition-colors ${
                                    active
                                      ? "border-sage-600 bg-sage-600 text-cream-50"
                                      : "border-cream-300 text-charcoal hover:border-sage-600/60"
                                  }`}
                                >
                                  {j}
                                </button>
                              );
                            })}
                          </div>
                          <input
                            type="text"
                            name="jurisdiction"
                            value={form.jurisdiction}
                            onChange={onChange}
                            placeholder="Or type another jurisdiction"
                            data-testid="intake-input-jurisdiction"
                            className="mt-4 w-full bg-transparent border-b border-cream-300 focus:border-sage-600 focus:outline-none px-0 py-3 font-sans text-charcoal placeholder:text-charcoal-muted/60 transition-colors"
                          />
                        </div>
                        <div>
                          <label className="text-[11px] uppercase tracking-[0.22em] text-charcoal-muted font-sans block">
                            Brief context
                          </label>
                          <textarea
                            name="context"
                            value={form.context}
                            onChange={onChange}
                            rows={4}
                            placeholder="What stage is the matter at? What do you need from counsel?"
                            data-testid="intake-input-context"
                            className="mt-3 w-full bg-transparent border-b border-cream-300 focus:border-sage-600 focus:outline-none px-0 py-3 font-sans text-charcoal placeholder:text-charcoal-muted/60 resize-none transition-colors"
                          />
                        </div>
                      </div>
                    </Step>
                  )}

                  {step === 3 && (
                    <Step
                      eyebrow="Step 04"
                      title="Lastly, who are we speaking with?"
                      hint="We'll reply within one business day."
                    >
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                        <Field
                          label="Full name"
                          name="name"
                          required
                          value={form.name}
                          onChange={onChange}
                          placeholder="Your name"
                          testid="intake-input-name"
                        />
                        <Field
                          label="Email"
                          name="email"
                          type="email"
                          required
                          value={form.email}
                          onChange={onChange}
                          placeholder="you@company.com"
                          testid="intake-input-email"
                        />
                        <Field
                          label="Company / Fund"
                          name="company"
                          value={form.company}
                          onChange={onChange}
                          placeholder="Optional"
                          testid="intake-input-company"
                        />
                        <Field
                          label="Phone"
                          name="phone"
                          value={form.phone}
                          onChange={onChange}
                          placeholder="Optional"
                          testid="intake-input-phone"
                        />
                      </div>
                    </Step>
                  )}

                  {step === 4 && result && (
                    <SuccessStep
                      result={result}
                      calendlyUrl={result.calendly_url || CALENDLY_URL}
                      onReset={() => {
                        setForm(initialForm);
                        setResult(null);
                        setStep(0);
                      }}
                      goHome={() => navigate("/")}
                    />
                  )}
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Step controls */}
            {step < 4 && (
              <div className="mt-12 pt-8 border-t border-cream-300 flex items-center justify-between">
                <button
                  type="button"
                  onClick={back}
                  disabled={step === 0}
                  data-testid="intake-back-button"
                  className="inline-flex items-center gap-2 font-sans text-[12px] uppercase tracking-[0.22em] text-charcoal-muted hover:text-sage-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <ArrowLeft size={14} /> Back
                </button>

                {step < 3 ? (
                  <button
                    type="button"
                    onClick={next}
                    data-testid="intake-next-button"
                    disabled={!canAdvance}
                    className="inline-flex items-center gap-3 bg-sage-600 text-cream-50 hover:bg-sage-700 disabled:opacity-50 disabled:cursor-not-allowed px-7 py-4 font-sans text-[12px] uppercase tracking-[0.22em] transition-colors"
                  >
                    Continue <ArrowRight size={16} />
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={submit}
                    disabled={loading || !canAdvance}
                    data-testid="intake-submit-button"
                    className="inline-flex items-center gap-3 bg-sage-600 text-cream-50 hover:bg-sage-700 disabled:opacity-60 disabled:cursor-not-allowed px-7 py-4 font-sans text-[12px] uppercase tracking-[0.22em] transition-colors"
                  >
                    {loading ? (
                      <>
                        <Loader2 size={16} className="animate-spin" /> Submitting
                      </>
                    ) : (
                      <>
                        Submit intake <ArrowRight size={16} />
                      </>
                    )}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      <footer className="border-t border-cream-300 py-10">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <p className="font-sans text-[12px] text-charcoal-muted">
            © {new Date().getFullYear()} {FIRM.name}. Confidential intake.
          </p>
          <p className="font-sans text-[11px] uppercase tracking-[0.22em] text-charcoal-muted">
            Offices · Lagos
          </p>
        </div>
      </footer>
    </main>
  );
}

/* ---------- Sub-components ---------- */

function Step({ eyebrow, title, hint, children }) {
  return (
    <div>
      <span className="text-[11px] uppercase tracking-[0.28em] text-sage-600 font-sans">
        {eyebrow}
      </span>
      <h2 className="mt-3 font-serif text-3xl lg:text-[42px] leading-[1.08] tracking-tight">
        {title}
      </h2>
      {hint && (
        <p className="mt-3 text-charcoal-muted font-sans text-[15px] leading-relaxed max-w-xl">
          {hint}
        </p>
      )}
      <div className="mt-10">{children}</div>
    </div>
  );
}

function Field({ label, name, value, onChange, type = "text", placeholder, required, testid }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-[0.22em] text-charcoal-muted font-sans block">
        {label}
        {required && <span className="text-sage-600"> *</span>}
      </label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        data-testid={testid}
        className="mt-3 w-full bg-transparent border-b border-cream-300 focus:border-sage-600 focus:outline-none px-0 py-3 font-sans text-charcoal placeholder:text-charcoal-muted/60 transition-colors"
      />
    </div>
  );
}

function SuccessStep({ result, calendlyUrl, onReset, goHome }) {
  const badgeStyles = {
    Priority: "bg-sage-700 text-cream-50",
    Standard: "bg-sage-100 text-sage-700",
    Exploratory: "bg-cream-200 text-charcoal-muted",
  };
  return (
    <div data-testid="intake-success">
      <span className="text-[11px] uppercase tracking-[0.28em] text-sage-600 font-sans">
        Received
      </span>
      <h2 className="mt-3 font-serif text-3xl lg:text-[42px] leading-[1.08] tracking-tight">
        Thank you. The firm has your intake.
      </h2>
      <p className="mt-4 text-charcoal-muted font-sans text-[15px] leading-relaxed max-w-xl">
        A confirmation has been sent to your email. To accelerate things, choose
        a time on the partners' calendar below.
      </p>

      <div className="mt-6 inline-flex items-center gap-2">
        <span
          data-testid="intake-lead-score"
          className={`inline-block px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] font-sans ${
            badgeStyles[result.lead_score] || badgeStyles.Standard
          }`}
        >
          {result.lead_score} lead
        </span>
        <span className="text-[11px] uppercase tracking-[0.22em] text-charcoal-muted font-sans">
          Ref · {result.id.slice(0, 8)}
        </span>
      </div>

      {/* Calendly embed */}
      <div className="mt-12">
        <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.22em] text-sage-600 font-sans">
          <CalendarClock size={16} /> Book a consultation
        </div>
        <div className="mt-4 border border-cream-300 bg-cream-50">
          <iframe
            src={`${calendlyUrl}?hide_event_type_details=0&hide_gdpr_banner=1&background_color=F9F8F6&primary_color=4A6B5D&text_color=1A2421`}
            title="Book a consultation"
            data-testid="calendly-iframe"
            className="w-full"
            style={{ minHeight: 720, border: 0 }}
            loading="lazy"
          />
        </div>
        <p className="mt-3 text-[12px] text-charcoal-muted font-sans">
          Trouble loading the calendar?{" "}
          <a
            href={calendlyUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="calendly-fallback-link"
            className="text-sage-700 hover:text-sage-600 underline underline-offset-4"
          >
            Open in a new tab
          </a>
          .
        </p>
      </div>

      <div className="mt-12 pt-8 border-t border-cream-300 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <button
          type="button"
          onClick={goHome}
          data-testid="intake-back-home"
          className="inline-flex items-center gap-2 font-sans text-[12px] uppercase tracking-[0.22em] text-charcoal hover:text-sage-700 transition-colors"
        >
          <ArrowLeft size={14} /> Back to home
        </button>
        <button
          type="button"
          onClick={onReset}
          data-testid="intake-submit-another"
          className="inline-flex items-center gap-3 border border-sage-600 text-charcoal hover:bg-sage-600 hover:text-cream-50 px-6 py-3 font-sans text-[11px] uppercase tracking-[0.22em] transition-colors"
        >
          Submit another intake
        </button>
      </div>
    </div>
  );
}
